import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import CastList from './components/CastList';

interface Therapist {
  id: string;
  name: string;
  nameEn: string;
  catchphrase: string;
  profile: string;
  specialties: string[];
  image: string;
}

interface DaySchedule {
  date: string;
  day: string;
  therapists: string[];
}

export default function Shift() {

  const therapists: Therapist[] = [
    {
      id: 'miu',
      name: 'みう',
      nameEn: 'Miu',
      catchphrase: 'ふんわり癒し系、静かなご褒美時間♡',
      profile: '落ち着いた雰囲気で、丁寧に寄り添うタイプ。はじめての方も安心して過ごせるように、会話の距離感も大切にします。',
      specialties: ['やさしいオイルトリートメント', '肩・首まわりのケア', 'リラックス空間づくり'],
      image: 'https://static.readdy.ai/image/7a6803503a084ffa064c71a8e4677aa3/bab936c5f8b69b54b0f3815e2b8b7b3f.jpeg',
    },
    {
      id: 'hinano',
      name: 'ひなの',
      nameEn: 'Hinano',
      catchphrase: '明るめほんわか、気持ちがふっと軽くなる✨',
      profile: 'にこっと明るい空気感で、緊張をほどくのが得意。テンポよく、でも品よく。癒しと元気のちょうどいいバランスです。',
      specialties: ['全身のめぐりケア', 'ふくらはぎ・脚まわり', 'すっきり仕上げ'],
      image: 'https://static.readdy.ai/image/7a6803503a084ffa064c71a8e4677aa3/c3272513a9ffb7248b7838e81f19a77b.jpeg',
    },
    {
      id: 'sakura',
      name: 'さくら',
      nameEn: 'Sakura',
      catchphrase: '上品クール寄り、さらっと大人の癒し',
      profile: '静かに整う"自分メンテ時間"を作りたい方へ。無理に話しかけず、落ち着いた空気でゆったり過ごせます。',
      specialties: ['背中〜腰のケア', '深呼吸できるリズム', '余白のある接客'],
      image: 'https://static.readdy.ai/image/7a6803503a084ffa064c71a8e4677aa3/8af176a9fda17920039a8eaf6a9bec9e.jpeg',
    },
    {
      id: 'reina',
      name: 'れいな',
      nameEn: 'Reina',
      catchphrase: 'やさしく包み込む、安心感のある時間♡',
      profile: '聞き上手で、自然体でいられる雰囲気。お疲れ気味の日も、気づいたら肩の力が抜けているような時間を目指します。',
      specialties: ['肩甲骨まわり', '手先のケア', 'まったりリラックス'],
      image: 'https://static.readdy.ai/image/7a6803503a084ffa064c71a8e4677aa3/e223cb772c8147fde95bd0ce85d5ff74.jpeg',
    },
  ];

  const weekSchedule: DaySchedule[] = [
    { date: '3/1', day: '日', therapists: ['みう', 'ひなの'] },
    { date: '3/2', day: '月', therapists: ['さくら', 'れいな'] },
    { date: '3/3', day: '火', therapists: ['ひなの', 'さくら'] },
    { date: '3/4', day: '水', therapists: ['みう', 'れいな'] },
    { date: '3/5', day: '木', therapists: ['みう', 'さくら'] },
    { date: '3/6', day: '金', therapists: ['ひなの', 'れいな'] },
    { date: '3/7', day: '土', therapists: ['さくら', 'みう'] },
  ];

  const getTodayTherapists = () => {
    const today = new Date().getDay();
    const scheduleIndex = today === 0 ? 6 : today - 1;
    return weekSchedule[scheduleIndex]?.therapists || [];
  };

  const todayTherapists = getTodayTherapists();

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-10 bg-gradient-to-br from-pink-50 via-white to-purple-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Shift</h1>
          <p className="text-pink-500 text-xl mb-3">出勤情報</p>
          <p className="text-gray-500 text-sm">今週のご案内です♡</p>
        </div>
      </section>

      {/* 週間スケジュール */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Weekly Schedule</h2>
            <p className="text-pink-500 text-lg">今週の出勤予定</p>
            <p className="text-gray-500 text-sm mt-2">全日 11:00〜6:00</p>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-pink-50 to-purple-50">
                  <th className="border border-pink-200 px-4 py-4 text-gray-800 font-bold text-sm">日付</th>
                  <th className="border border-pink-200 px-4 py-4 text-gray-800 font-bold text-sm">曜日</th>
                  <th className="border border-pink-200 px-4 py-4 text-gray-800 font-bold text-sm">出勤セラピスト</th>
                  <th className="border border-pink-200 px-4 py-4 text-gray-800 font-bold text-sm">時間</th>
                </tr>
              </thead>
              <tbody>
                {weekSchedule.map((schedule, index) => (
                  <tr key={index} className="hover:bg-pink-50 transition-colors duration-200">
                    <td className="border border-pink-200 px-4 py-4 text-center text-gray-700 text-sm">{schedule.date}</td>
                    <td className="border border-pink-200 px-4 py-4 text-center text-gray-700 font-semibold text-sm">{schedule.day}</td>
                    <td className="border border-pink-200 px-4 py-4 text-center">
                      <div className="flex flex-wrap justify-center gap-2">
                        {schedule.therapists.map((name) => (
                          <span key={name} className="inline-block px-3 py-1 bg-pink-100 text-pink-600 rounded-full text-sm font-medium">
                            {name}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="border border-pink-200 px-4 py-4 text-center text-gray-600 text-sm">11:00〜6:00</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {weekSchedule.map((schedule, index) => (
              <div key={index} className="bg-white border-2 border-pink-200 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-gray-800">{schedule.date}</span>
                    <span className="px-3 py-1 bg-pink-100 text-pink-600 rounded-full text-sm font-semibold">{schedule.day}</span>
                  </div>
                  <span className="text-sm text-gray-500">11:00〜6:00</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {schedule.therapists.map((name) => (
                    <span key={name} className="inline-block px-4 py-2 bg-gradient-to-r from-pink-50 to-purple-50 text-pink-600 rounded-full text-sm font-medium">
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* セラピスト紹介 + 全員一覧 */}
      <section className="py-20 bg-gradient-to-br from-pink-50 via-purple-50 to-pink-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Our Therapists</h2>
            <p className="text-pink-500 text-lg">セラピスト紹介</p>
            <p className="text-gray-500 text-sm mt-2">全員20歳以上</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {therapists.map((therapist) => {
              const isWorkingToday = todayTherapists.includes(therapist.name);
              return (
                <div key={therapist.id} className="bg-white rounded-2xl border border-pink-100 overflow-hidden hover:border-pink-200 transition-all duration-300">
                  <div className="relative w-full h-80 bg-gradient-to-br from-pink-50 to-purple-50">
                    <img
                      src={therapist.image}
                      alt={`${therapist.name}さん`}
                      className="w-full h-full object-cover object-top"
                    />
                    {isWorkingToday && (
                      <div className="absolute top-4 right-4 px-4 py-2 bg-pink-500 text-white rounded-full text-sm font-bold flex items-center gap-1 whitespace-nowrap">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-checkbox-circle-fill text-base"></i>
                        </div>
                        本日出勤
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="mb-4">
                      <h3 className="text-2xl font-bold text-gray-800 mb-1">{therapist.name}</h3>
                      <p className="text-sm text-gray-500">{therapist.nameEn}</p>
                    </div>
                    <p className="text-pink-500 text-sm font-semibold mb-4">{therapist.catchphrase}</p>
                    <p className="text-gray-600 text-sm leading-relaxed mb-5">{therapist.profile}</p>
                    <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-4">
                      <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                        <div className="w-4 h-4 flex items-center justify-center mr-2">
                          <i className="ri-heart-line text-pink-500 text-base"></i>
                        </div>
                        得意なこと
                      </h4>
                      <ul className="space-y-2">
                        {therapist.specialties.map((specialty, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-700">
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5 mr-2">
                              <i className="ri-checkbox-circle-fill text-pink-400 text-xs"></i>
                            </div>
                            {specialty}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 在籍セラピスト全員一覧 */}
          <div className="border-t border-pink-200 pt-16">
            <CastList />
          </div>
        </div>
      </section>

      {/* 予約導線 */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Reservation</h2>
            <p className="text-pink-500 text-lg">ご予約の流れ</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { step: '1', icon: 'ri-calendar-line', title: '日時を選ぶ', desc: 'ご希望の日時とコースを選択' },
              { step: '2', icon: 'ri-user-heart-line', title: 'セラピストを選ぶ', desc: '本指名・フリーから選択可能' },
              { step: '3', icon: 'ri-checkbox-circle-line', title: '予約完了', desc: '当日お待ちしております♡' },
            ].map((item) => (
              <div key={item.step} className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-6 text-center border border-pink-100">
                <div className="w-12 h-12 flex items-center justify-center bg-pink-500 text-white rounded-full text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <i className={`${item.icon} text-4xl text-pink-500`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <a
              href="https://r.caskan.jp/jdspa"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-12 py-5 bg-gradient-to-r from-pink-500 to-pink-400 text-white text-lg rounded-full font-bold hover:shadow-2xl transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-6 h-6 flex items-center justify-center mr-3">
                <i className="ri-calendar-check-line text-2xl"></i>
              </div>
              WEB予約へ進む
            </a>
            <p className="text-gray-400 text-sm mt-4">24時間いつでもご予約いただけます♡</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}